import { Heading } from '@mm/components';
import { useDebounce } from '@mm/hooks';
import { isContactMessage } from '@mm/state/model/ContactMessageModel';
import {
  ConversationDirection,
  LivechatMessageModel,
  MessageKind,
} from '@mm/state/model/LivechatMessageModel';
import { useActor } from '@xstate/react';
import conversation from 'assets/conversation.svg';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useContextSelector } from 'use-context-selector';
import { ContactContext, EmptySelectedContact } from '../../contactMachine';
import { ConversationContext } from '../../conversationMachine';
import { LiveChatContext } from '../../LiveChatContext';
import {
  ConversationSession,
  getConversationSessionsFromMessages,
  getFormattedDate,
} from '../../shared';
import { ChannelIcon } from '../ChannelIcon';
import { IncomingMessage } from './IncomingMessage/IncomingMessage';
import './Messages.pcss';
import { OutgoingMessage } from './OutgoingMessage/OutgoingMessage';
import { UnsentMessage } from './UnsentMessage/UnsentMessage';

export const Messages = () => {
  const lcService = useContextSelector(
    LiveChatContext,
    v => v.lcServiceRef.current!,
  );

  const [needsToScrollToBottom, setNeedsToScrollToBottom] = useState(true);
  const [preservedScrollHeight, setPreservedScrollHeight] = useState(0);
  const [hoverMessageKey, setHoverMessageKey] = useState<string | undefined>(
    undefined,
  );
  const [messagesBySession, setMessagesBySession] = useState<
    ConversationSession[]
  >([]);
  const [showEmptyMessagesText, setShowEmptyMessagesText] = useState(false);
  const showEmptyMessagesTextDebounced = useDebounce(showEmptyMessagesText, 50);
  const [isEmptyStateCheckOn, setIsEmptyStateCheckOn] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setIsEmptyStateCheckOn(true), 1000);
    return () => clearTimeout(id);
  });

  const [lcMachineState] = useActor(lcService);

  const { remoteUserId } = lcMachineState.context || {};

  const selectedContact =
    (lcMachineState.context.remoteUserId
      ? (
          lcMachineState.context.contactsRefs[
            lcMachineState.context.remoteUserId
          ]?.state.context as ContactContext
        )?.model
      : undefined) || EmptySelectedContact;

  const selectedContactMachine = remoteUserId
    ? lcMachineState.context.contactsRefs[remoteUserId]
    : undefined;

  const refreshSelectedContact = useCallback(() => {
    selectedContactMachine?.send({
      type: 'LC_CONTACT_REFRESH',
      payload: remoteUserId || '',
    });
  }, [selectedContactMachine?.id, remoteUserId]);

  const conversationMachine = remoteUserId
    ? lcMachineState.context.conversationsRefs[remoteUserId]
    : undefined;

  const messagesChangingFast = [
    ...((conversationMachine?.state.context as ConversationContext)
      ?.allMessages || []),
  ];

  // Note: very strange, but without debouncing React misses the changes in useEffect
  // even though console.log correctly shows length of the object
  const messages = useDebounce(messagesChangingFast, 47);

  const optimisticMessages =
    (conversationMachine?.state.context as ConversationContext)
      ?.optimisticMessages || [];

  const noMoreMessages = !(
    conversationMachine?.state.context as ConversationContext
  )?.messagesPageState.pageState;
  const isFetchingMessages =
    conversationMachine?.state.matches('awaitingMessages');

  const messagesScrollRef = useRef<HTMLDivElement>(null);

  const scrollMessagesToBottom = () => {
    setTimeout(() => {
      if (messagesScrollRef.current) {
        const { scrollHeight } = messagesScrollRef.current;
        messagesScrollRef.current.scrollTo(0, scrollHeight);
      }
    }, 25);
  };
  const restorePreservedScroll = () => {
    if (messagesScrollRef.current) {
      messagesScrollRef.current.scrollTop =
        messagesScrollRef.current.scrollHeight - preservedScrollHeight;
    }
  };

  const isMessagesScrollAtBottom = () => {
    if (messagesScrollRef.current) {
      return (
        messagesScrollRef.current.clientHeight +
          messagesScrollRef.current.scrollTop >=
        messagesScrollRef.current.scrollHeight - 10
      );
    }
    return true;
  };

  const latestMessageRead = messages.find(m => m.kind === MessageKind.read);
  const latestIncomingMsg = messages.find(
    m => m.direction === 1 && m.kind !== MessageKind.delivery,
  );

  useEffect(() => {
    if (!isMessagesScrollAtBottom() && needsToScrollToBottom) {
      setNeedsToScrollToBottom(false);
      scrollMessagesToBottom();
    } else if (preservedScrollHeight) {
      restorePreservedScroll();
      setPreservedScrollHeight(0);
    }
  }, [JSON.stringify(messagesBySession)]);

  useEffect(() => {
    setNeedsToScrollToBottom(true);
    scrollMessagesToBottom();
  }, [remoteUserId]);

  useEffect(() => {
    // Note: This guards the component from showing data belonging to a different user.
    // It is hard to accomplish same behavior in the subscriptions part.
    if (messages && messages[0] && messages[0].remoteUserId !== remoteUserId) {
      setMessagesBySession([]);
      return;
    }
    const messagesWithoutNotificatons = messages.filter(m => {
      if (
        m.kind === MessageKind.read &&
        remoteUserId?.toString().startsWith('em_')
      ) {
        return true;
      }
      return ![MessageKind.read, MessageKind.delivery, MessageKind.fail].some(
        k => m.kind === k,
      );
    });

    const conversations = getConversationSessionsFromMessages(
      messagesWithoutNotificatons,
      optimisticMessages,
      true,
      noMoreMessages,
    );
    setMessagesBySession(conversations);

    if (messagesScrollRef.current) {
      if (isMessagesScrollAtBottom()) {
        setNeedsToScrollToBottom(true);
      } else if (messagesScrollRef.current.scrollTop === 0) {
        setPreservedScrollHeight(messagesScrollRef.current.scrollHeight);
      }
    }
  }, [
    messagesChangingFast.length,
    JSON.stringify(messages),
    remoteUserId,
    JSON.stringify(optimisticMessages),
    noMoreMessages,
  ]);

  const onScroll = useCallback(
    (e: React.SyntheticEvent<HTMLDivElement>) => {
      if (e.currentTarget) {
        const atTop = e.currentTarget.scrollTop === 0;

        if (atTop && !isFetchingMessages) {
          conversationMachine?.send({
            type: 'LC_CONVERSATION_MESSAGES_FETCH_MORE',
          });
        }
      }
    },
    [isFetchingMessages, remoteUserId],
  );

  useEffect(() => {
    if (isEmptyStateCheckOn) {
      setShowEmptyMessagesText(
        !isFetchingMessages && messagesBySession.length === 0,
      );
    }
  }, [isEmptyStateCheckOn, isFetchingMessages, messagesBySession.length]);

  // Note: Please note that the order of elements is reversed via CSS
  const memoMessages = useMemo(() => {
    if (!selectedContact) {
      return [];
    }

    const emailMessagesReadTimeSet = Object.fromEntries(
      !remoteUserId?.startsWith('em_')
        ? []
        : messages
            .filter(
              m =>
                m.kind === MessageKind.read &&
                m.direction === 1 &&
                m.structure.emailTimestamp,
            )
            .map(m => [m.structure.emailTimestamp, m.time]),
    );

    const { profilePic, fullName, variables, id: contactId } = selectedContact;

    return messagesBySession.map((c, idx) => (
      <div key={`session-${idx}`} className="live-chat--messages-conversation">
        <div className="live-chat--messages-conversation__time">
          <div className="live-chat--messages-conversation__time-hr" />
          <div className="live-chat--messages-conversation__time-text">
            <div className="live-chat--messages-conversation__time-icon">
              <ChannelIcon remoteUserId={remoteUserId} />
            </div>
            {getFormattedDate(c.sessionFirstMessageAt)}
          </div>
          <div className="live-chat--messages-conversation__time-hr" />
        </div>
        {c.sessionMessages &&
          c.sessionMessages.map((m, i) => {
            const messageKey = `${idx}-${i}`;
            const isFirstMessage = i === 0;
            const nextMessage = c.sessionMessages[i + 1];
            const prevOutgoingMessage = c.sessionMessages
              .slice(0, i)
              .reverse()
              .find(
                (m: LivechatMessageModel) =>
                  m.direction === 2 && m.currentPageId,
              ) as LivechatMessageModel | undefined;

            if (isContactMessage(m)) {
              return <UnsentMessage message={m} />;
            }

            const read =
              m.readAt ||
              m.botId === '0' || // imported messages
              emailMessagesReadTimeSet[m.time] ||
              (latestMessageRead && m && m.time < latestMessageRead.time) ||
              (latestIncomingMsg && m.time < latestIncomingMsg.time);

            const messageWithReadAtOverridden = {
              ...m,
              readAt:
                m.readAt ||
                (emailMessagesReadTimeSet[m.time]
                  ? new Date(
                      parseInt(emailMessagesReadTimeSet[m.time], 10),
                    ).toISOString()
                  : undefined),
            };

            return m.direction === ConversationDirection.incoming ? (
              <IncomingMessage
                key={messageKey}
                messages={messages}
                message={m}
                isFirstMessage={isFirstMessage}
                nextMessage={nextMessage}
                messageKey={messageKey}
                hoverMessageKey={hoverMessageKey}
                setHoverMessageKey={setHoverMessageKey}
                profilePic={profilePic}
                fullName={fullName}
                variables={variables}
                contactId={contactId}
                refreshSelectedContact={refreshSelectedContact}
              />
            ) : (
              <OutgoingMessage
                key={messageKey}
                messages={messages}
                message={messageWithReadAtOverridden}
                prevOutgoingMessage={prevOutgoingMessage}
                isFirstMessage={isFirstMessage}
                messageKey={messageKey}
                profilePic={profilePic}
                isFinalMessage={idx === 0 && i === 0}
                read={read}
              />
            );
          })}
      </div>
    ));
  }, [
    selectedContact,
    JSON.stringify(messagesBySession),
    remoteUserId,
    hoverMessageKey,
    refreshSelectedContact,
    latestMessageRead,
    latestIncomingMsg,
  ]);

  if (!selectedContact) {
    return null;
  }

  return (
    <div
      className="live-chat--messages"
      onScroll={onScroll}
      ref={messagesScrollRef}
    >
      {showEmptyMessagesTextDebounced && (
        <div className="live-chat--messages-container_empty">
          <img
            src={conversation}
            className="live-chat--messages-container-image"
          />
          <Heading className="live-chat--messages-container-text" size={6}>
            <p>
              You haven't messaged with this contact yet. Say "Hello" to them
              below &#x1F447; &#x1F447;
            </p>
          </Heading>
        </div>
      )}
      {memoMessages}
    </div>
  );
};
